import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";



/* ─── types ─────────────────────────────────────────────── */
interface AutoRule {
  id: number;
  meter_id: number;
  meter_serial: string;
  unit_label: string;
  threshold_units: number;
  topup_amount: number;
  phone_number: string;
  enabled: boolean;
  last_triggered: string | null;
  trigger_count: number;
}

interface Meter {
  id: number;
  serial_number: string;
  balance?: { balance_units: number };
  unit?: { label: string };
  property?: { token_value: number; unit_label: string };
}

/* ─── helpers ───────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? "bg-sky-500" : "bg-gray-200"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow-md transform transition-transform ${value ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

const PRESET_AMOUNTS = [100, 200, 500, 1000, 2000];
const PRESET_THRESHOLDS = [5, 10, 15, 20, 30];

/* ─── modal ─────────────────────────────────────────────── */
function RuleModal({
  meter,
  existing,
  onClose,
  onSave,
}: {
  meter: Meter | null;
  existing: AutoRule | null;
  onClose: () => void;
  onSave: (rule: Partial<AutoRule>) => void;
}) {
  const { user } = useAuthStore();
  const [threshold, setThreshold] = useState(existing?.threshold_units ?? 10);
  const [amount, setAmount] = useState(existing?.topup_amount ?? 200);
  const [phone, setPhone] = useState(existing?.phone_number ?? user?.phone_number ?? "");
  const [customThreshold, setCustomThreshold] = useState(false);
  const [customAmount, setCustomAmount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const units = meter?.property?.token_value ? amount / meter.property.token_value : 0;

  const handleSave = async () => {
    if (!phone.match(/^(\+254|07|01)\d{8,9}$/)) {
      setError("Enter a valid Kenyan phone number (e.g. 0712345678)");
      return;
    }
    setSaving(true);
    setError("");
    onSave({ meter_id: meter?.id, threshold_units: threshold, topup_amount: amount, phone_number: phone });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        {/* header */}
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">
                {existing ? "Edit Auto Top-Up Rule" : "Create Auto Top-Up Rule"}
              </h2>
              {meter && (
                <p className="text-sky-300/70 text-[12px] mt-0.5">Meter · {meter.serial_number}</p>
              )}
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* threshold */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              Trigger when balance drops below
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_THRESHOLDS.map((t) => (
                <button
                  key={t}
                  onClick={() => { setThreshold(t); setCustomThreshold(false); }}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                    threshold === t && !customThreshold
                      ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                  }`}
                >
                  {t} units
                </button>
              ))}
              <button
                onClick={() => setCustomThreshold(true)}
                className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                  customThreshold ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                }`}
              >
                Custom
              </button>
            </div>
            {customThreshold && (
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                placeholder="Enter units"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            )}
          </div>

          {/* top-up amount */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              Auto top-up amount (KES)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => { setAmount(a); setCustomAmount(false); }}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                    amount === a && !customAmount
                      ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                  }`}
                >
                  KES {a.toLocaleString()}
                </button>
              ))}
              <button
                onClick={() => setCustomAmount(true)}
                className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                  customAmount ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                }`}
              >
                Custom
              </button>
            </div>
            {customAmount && (
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                placeholder="Enter amount"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            )}

            {/* calculator tile */}
            {meter?.property?.token_value && (
              <div className="mt-2.5 flex items-center gap-3 bg-sky-50 border border-sky-100 rounded-xl px-4 py-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-4 h-4 flex-shrink-0">
                  <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="text-[12.5px] text-sky-700">
                  KES {amount.toLocaleString()} = <strong>{units.toFixed(1)} {meter.property.unit_label}</strong> of water
                </p>
              </div>
            )}
          </div>

          {/* phone */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              M-Pesa Phone Number
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-gray-400">🇰🇪</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0712 345 678"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">M-Pesa STK push will be sent to this number when triggered.</p>
          </div>

          {error && (
            <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* summary */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-[12.5px] text-gray-600 leading-relaxed">
            <strong className="text-gray-800">Summary:</strong> When your balance drops below{" "}
            <strong className="text-sky-600">{threshold} units</strong>, we'll automatically send a{" "}
            <strong className="text-emerald-600">KES {amount.toLocaleString()} M-Pesa</strong> prompt to{" "}
            <strong className="text-gray-800">{phone || "your phone"}</strong>.
          </div>

          {/* actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : existing ? "Update Rule" : "Create Rule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── rule card ─────────────────────────────────────────── */
function RuleCard({
  rule,
  onToggle,
  onEdit,
  onDelete,
}: {
  rule: AutoRule;
  onToggle: (id: number, enabled: boolean) => void;
  onEdit: (rule: AutoRule) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-5 transition-all ${rule.enabled ? "border-sky-100" : "border-gray-100 opacity-70"}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${rule.enabled ? "bg-sky-50" : "bg-gray-100"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke={rule.enabled ? "#0ea5e9" : "#9ca3af"} strokeWidth={1.8} className="w-5 h-5">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-bold text-gray-800 truncate">Meter · {rule.meter_serial}</p>
            <p className="text-[11.5px] text-gray-400 mt-0.5">{rule.unit_label}</p>
          </div>
        </div>
        <Toggle value={rule.enabled} onChange={(v) => onToggle(rule.id, v)} />
      </div>

      {/* rule details grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {[
          { label: "Trigger Below", value: `${rule.threshold_units} units` },
          { label: "Top-Up Amount", value: `KES ${rule.topup_amount.toLocaleString()}` },
          { label: "M-Pesa Phone", value: rule.phone_number || "—" },
          { label: "Times Triggered", value: rule.trigger_count.toString() },
        ].map((d) => (
          <div key={d.label} className="bg-gray-50 rounded-xl px-3 py-2.5">
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider font-medium">{d.label}</p>
            <p className="text-[13px] font-semibold text-gray-800 mt-0.5">{d.value}</p>
          </div>
        ))}
      </div>

      {rule.last_triggered && (
        <p className="text-[11.5px] text-gray-400 mb-4">
          Last triggered: {new Date(rule.last_triggered).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      )}

      {/* actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(rule)}
          className="flex-1 py-2 rounded-xl border border-gray-200 text-[12.5px] font-semibold text-gray-600 hover:bg-gray-50 transition-all"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(rule.id)}
          className="flex-1 py-2 rounded-xl border border-red-100 text-[12.5px] font-semibold text-red-500 hover:bg-red-50 transition-all"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function AutoDeductionsPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [rules, setRules] = useState<AutoRule[]>([
    // seeded mock rules so UI is demonstrable without backend endpoints
    {
      id: 1, meter_id: 1, meter_serial: "MTR-001", unit_label: "Litres",
      threshold_units: 10, topup_amount: 500, phone_number: user?.phone_number ?? "0712345678",
      enabled: true, last_triggered: new Date(Date.now() - 2 * 86400000).toISOString(), trigger_count: 4,
    },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState<AutoRule | null>(null);
  const [modalMeter, setModalMeter] = useState<Meter | null>(null);

  useEffect(() => {
    api.get(`/meters/`)
      .then((r) => setMeters(r.data?.results ?? r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openCreate = (meter: Meter) => {
    setEditRule(null);
    setModalMeter(meter);
    setModalOpen(true);
  };

  const openEdit = (rule: AutoRule) => {
    const m = meters.find((x) => x.id === rule.meter_id) ?? null;
    setEditRule(rule);
    setModalMeter(m);
    setModalOpen(true);
  };

  const handleSave = (data: Partial<AutoRule>) => {
    if (editRule) {
      setRules((prev) => prev.map((r) => r.id === editRule.id ? { ...r, ...data } : r));
    } else {
      const newRule: AutoRule = {
        id: Date.now(),
        meter_id: data.meter_id!,
        meter_serial: meters.find((m) => m.id === data.meter_id)?.serial_number ?? "—",
        unit_label: meters.find((m) => m.id === data.meter_id)?.property?.unit_label ?? "units",
        threshold_units: data.threshold_units!,
        topup_amount: data.topup_amount!,
        phone_number: data.phone_number!,
        enabled: true,
        last_triggered: null,
        trigger_count: 0,
      };
      setRules((prev) => [...prev, newRule]);
    }
  };

  const handleToggle = (id: number, enabled: boolean) => {
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, enabled } : r));
  };

  const handleDelete = (id: number) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const metersWithoutRule = meters.filter((m) => !rules.some((r) => r.meter_id === m.id));

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Auto Top-Up</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">Never run out of water — auto-recharge when balance gets low</p>
          </div>
        </div>

        {/* ── how it works banner ── */}
        <div className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-5 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-bold text-[15px]">How Auto Top-Up Works</p>
              <p className="text-sky-100/80 text-[12.5px] mt-1 leading-relaxed">
                Set a balance threshold. When your water token balance drops below it, we automatically
                trigger an M-Pesa STK Push to your phone. Approve the prompt and your balance is instantly topped up.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {[
                { icon: "⚡", label: "Instant", sub: "SMS prompt" },
                { icon: "🔒", label: "Secure", sub: "M-Pesa PIN" },
                { icon: "♻️", label: "Automatic", sub: "No manual work" },
              ].map((f) => (
                <div key={f.label} className="bg-white/15 rounded-xl px-3 py-2.5 text-center min-w-[70px]">
                  <div className="text-xl">{f.icon}</div>
                  <p className="text-[11px] font-bold mt-1">{f.label}</p>
                  <p className="text-[10px] text-sky-100/70">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── active rules ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2].map((i) => <Skeleton key={i} className="h-56" />)}
          </div>
        ) : rules.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gray-50 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.5} className="w-7 h-7">
                <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="font-semibold text-gray-700 text-[14px]">No rules yet</p>
            <p className="text-[12.5px] text-gray-400 mt-1">Create a rule below to enable automatic top-ups.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-[14px] font-bold text-gray-700">Active Rules ({rules.length})</h2>
              <span className="text-[11.5px] text-gray-400">{rules.filter((r) => r.enabled).length} enabled</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onToggle={handleToggle}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </>
        )}

        {/* ── add rule for meter ── */}
        {metersWithoutRule.length > 0 && (
          <div>
            <h2 className="text-[14px] font-bold text-gray-700 mb-3">Set Up for a Meter</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {metersWithoutRule.map((m) => (
                <button
                  key={m.id}
                  onClick={() => openCreate(m)}
                  className="group bg-white rounded-2xl border border-dashed border-gray-200 hover:border-sky-300 hover:bg-sky-50/40 p-5 text-left transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-50 group-hover:bg-sky-100 flex items-center justify-center mb-3 transition-all">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="text-[13px] font-semibold text-gray-800">{m.serial_number}</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">Add auto top-up rule</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── history log ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="text-[14px] font-bold text-gray-800">Trigger History</h2>
            <p className="text-[12px] text-gray-400 mt-0.5">Recent auto top-up events</p>
          </div>
          <div className="divide-y divide-gray-50">
            {[
              { date: "10 Jun 2026, 08:14 AM", meter: "MTR-001", amount: 500, status: "success" },
              { date: "08 Jun 2026, 06:32 PM", meter: "MTR-001", amount: 500, status: "success" },
              { date: "05 Jun 2026, 11:05 AM", meter: "MTR-001", amount: 500, status: "failed" },
              { date: "01 Jun 2026, 09:20 AM", meter: "MTR-001", amount: 500, status: "success" },
            ].map((log, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === "success" ? "bg-emerald-400" : "bg-red-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-800">Meter {log.meter} — KES {log.amount.toLocaleString()}</p>
                  <p className="text-[11.5px] text-gray-400">{log.date}</p>
                </div>
                <span className={`text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full ${
                  log.status === "success" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                }`}>
                  {log.status === "success" ? "Completed" : "Failed"}
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── modal ── */}
      {modalOpen && (
        <RuleModal
          meter={modalMeter}
          existing={editRule}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
